/**
 * 供应商测试弹窗：选模板或输入消息，流式 / 普通两种方式发送，显示 AI 响应和性能指标
 */
import { useState } from 'react'
import { Alert, App, Button, Col, Divider, Empty, Input, Modal, Row, Select, Space, Spin, Statistic, Switch, Typography } from 'antd'
import type { useProviderTesting } from './useProviderTesting'
import type { AIProvider, PerformanceMetrics } from './types'
import styles from './AIProviderPage.module.css'

export const TEST_TEMPLATES = [
  { label: '基础能力', value: '介绍一下你自己' },
  { label: '中文理解', value: '解释“君子和而不同”的含义' },
  { label: '代码能力', value: '写一个Python快速排序' },
  { label: '推理能力', value: '如果所有的A都是B，所有的B都是C，那么A和C的关系是什么？' }
]

interface ProviderTestModalProps {
  provider: AIProvider | null
  testing: ReturnType<typeof useProviderTesting>
  onClose: () => void
}

function MetricsPanel({ metrics }: Readonly<{ metrics: PerformanceMetrics }>) {
  return (
    <div>
      <Divider />
      <Typography.Title level={5}>性能指标</Typography.Title>
      <Row gutter={[16, 8]} className={styles.metrics}>
        <Col span={6}>
          <Statistic title="首字响应" value={metrics.ttfb === null ? '-' : `${metrics.ttfb.toFixed(3)}s`} />
        </Col>
        <Col span={6}>
          <Statistic title="总时间" value={`${metrics.totalTime.toFixed(3)}s`} />
        </Col>
        <Col span={6}>
          <Statistic title="字符数" value={metrics.totalChars} />
        </Col>
        <Col span={6}>
          <Statistic title="速度" value={`${metrics.charsPerSec.toFixed(1)}字/秒`} />
        </Col>
      </Row>
    </div>
  )
}

// 每次打开都重新挂载，消息从当前选中的模板初始化
export function ProviderTestModal({ provider, testing, onClose }: Readonly<ProviderTestModalProps>) {
  if (!provider) return null
  return <TestPanel key={provider.id} provider={provider} testing={testing} onClose={onClose} />
}

function TestPanel({ provider, testing, onClose }: Readonly<ProviderTestModalProps & { provider: AIProvider }>) {
  const { message } = App.useApp()
  const [template, setTemplate] = useState(TEST_TEMPLATES[0].value)
  const [testMessage, setTestMessage] = useState(TEST_TEMPLATES[0].value)
  const [streamMode, setStreamMode] = useState(true)
  const { response, error, metrics, isStreaming } = testing

  const send = () => {
    if (!testMessage.trim()) {
      message.warning('请输入测试消息')
      return
    }
    void testing.sendTest(provider, testMessage, streamMode)
  }

  const close = () => {
    testing.reset()
    onClose()
  }

  const renderResponse = () => {
    if (testing.testing && !response && !error) {
      return (
        <div className={styles.loadingState}>
          <Spin />
          <span>正在连接AI服务器...</span>
        </div>
      )
    }
    if (!response && !error) return <Empty description="等待测试..." />
    return (
      <>
        {response && (
          <div className={styles.aiText} data-testid="ai-response">
            {response}
            {isStreaming && <span className={styles.cursor}>▊</span>}
          </div>
        )}
        {error && <Alert className={styles.responseError} type="error" showIcon title={`错误: ${error}`} />}
      </>
    )
  }

  return (
    <Modal open title={`测试连接 - ${provider.provider_name}`} width={800} footer={null} onCancel={close}>
      <Row gutter={16} align="middle">
        <Col span={8}>
          <Select
            aria-label="测试模板"
            className={styles.fullWidth}
            value={template}
            options={TEST_TEMPLATES}
            onChange={(value) => {
              setTemplate(value)
              setTestMessage(value)
            }}
          />
        </Col>
        <Col span={16} className={styles.alignRight}>
          <Space>
            <span>流式响应：</span>
            <Switch aria-label="流式响应" checked={streamMode} onChange={setStreamMode} />
          </Space>
        </Col>
      </Row>
      <Input.TextArea
        className={styles.testInput}
        rows={3}
        placeholder="输入测试消息..."
        value={testMessage}
        onChange={(event) => setTestMessage(event.target.value)}
      />
      <div className={styles.testActions}>
        <Space>
          <Button disabled={testing.testing} onClick={testing.reset}>
            清空
          </Button>
          <Button type="primary" loading={testing.testing} onClick={send}>
            发送
          </Button>
        </Space>
      </div>

      <Divider />

      <Typography.Title level={5}>AI响应</Typography.Title>
      <div className={isStreaming ? `${styles.responseBox} ${styles.streaming}` : styles.responseBox}>
        {renderResponse()}
      </div>

      {metrics && <MetricsPanel metrics={metrics} />}
    </Modal>
  )
}
